const getDefaultSectionsUrl = () =>
  globalThis.location.pathname + globalThis.location.search;
const rootUrl = globalThis.Shopify.routes.root;

import { getBundleItem } from '@/js/bundle-store.js';

// Internal (not-exported) methods

/**
 * Updates the value and count on the cart icon.
 * Uses passed in value, or else pulls from the cart.js endpoint
 *
 * @param {Number} passed_item_count - The number of items in the cart
 * @returns {void}
 */
const updateCartIcon = async passed_item_count => {
  const cartIcon = document.querySelector('cart-icon');
  const item_count = passed_item_count ?? (await fetchCartCount());

  cartIcon.setCount(item_count);
};

/**
 *
 * @returns {Number} - The number of items in the cart
 */
const fetchCartCount = async () => {
  try {
    const response = await fetch(rootUrl + 'cart.js');
    const data = await response.json();
    const { item_count } = data;

    return item_count;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

/**
 * Get the number of items in the cart for a given variant ID
 * @param {*} variantId
 * @returns {Number} - The number of items in the cart
 */
const getLineItemCount = async variantId => {
  try {
    const response = await fetch(rootUrl + 'cart.js');
    const cart = await response.json();
    return cart.items.find(item => Number(item.id) === Number(variantId))
      ?.quantity;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

// Exported methods

/**
 * Dispatch a custom event when the cart icon is clicked
 * @param {Object} element - The element to listen for clicks on
 * @returns {void}
 */
const dispatchCartClickEventListener = element => {
  element.addEventListener('click', event => {
    event.preventDefault();

    element.dispatchEvent(
      new CustomEvent('pxl:flyoutCart:toggle', {
        bubbles: true
      })
    );
  });
};

/**
 * Handle cart API response and update sections with dynamic content
 * @param {JSON} cartResponseJson - json() from the response from cart API
 */
const onCartUpdate = async ({ sections, cart_count }) => {
  // Add in a timeout to alow the CTA to update before the sections are updated
  setTimeout(async () => {
    updateSectionsFromCartResponse(sections);
  }, 500);
  updateCartIcon(cart_count);
};

/* 
Utility methods for modifying the cart
As a rule, these should all be async methods that return a Promise.
*/

/**
 * Update the quantity of an item in the cart by variant ID
 *
 * @param {Object} updates An object containing key:variantId, value: newQuantity{ 1121231231: 2, 231231313: 0 }
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const updateItemQuantityRequest = async (
  updates,
  sections = [],
  sections_url = getDefaultSectionsUrl()
) => {
  const response = await fetch(rootUrl + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      updates,
      sections,
      sections_url
    })
  });
  return response;
};

/**
 * Set the quantity of each variant passed to zero
 *
 * @param {Array.<Number>} items
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const removeVariantsRequest = async (
  items,
  sections = [],
  sections_url = getDefaultSectionsUrl()
) => {
  const updates = {};
  for (const variantId of items) {
    updates[variantId] = 0;
  }

  const response = await fetch(rootUrl + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      updates,
      sections,
      sections_url
    })
  });
  return response;
};

/**
 * Add item in the cart
 *
 *
 * @param {Array.<object>} items
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const addItemsToCartRequest = async (
  items,
  sections = [],
  sections_url = getDefaultSectionsUrl()
) => {
  const response = await fetch(rootUrl + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      items: items,
      sections,
      sections_url
    })
  });
  return response;
};

/**
 * Updates the contents of all the sections passed in
 * @param {object} sections - From the shopify API, the sections object is a key-value pair of section id and html string
 * @returns {void}
 */
const updateSectionsFromCartResponse = sections => {
  if (!sections) return;
  for (const [sectionKey, htmlString] of Object.entries(sections)) {
    const newElement = new DOMParser().parseFromString(htmlString, 'text/html');

    /**
     * @type {HTMLElement}
     * @augments {DynamicSectionElement}
     */
    const targetElement = document.querySelector(
      `[data-section-id="${sectionKey}"]`
    );
    if (targetElement) {
      targetElement.replaceContent(newElement, sectionKey);
    }
  }
};

/**
 * Alters the user quantity to account for max inventory quantity and bundle quantity
 * @param {Number} quantity - The quantity of the variant to add to the cart
 * @param {Number} maxInventoryQuantity - The max inventory quantity of the variant
 * @param {String} variantId - The ID of the variant to add to the cart
 * @param {Boolean} trackQuantity - Whether the item is tracking quantity
 * @param {Boolean} bundleable - Whether the item is bundleable
 * @param {Boolean} bundleToCart - Whether the item is being added from a bundle to the cart
 * @returns {Number} - The quantity to add to the cart
 */
const maxQuantityHandler = async (
  quantity,
  maxInventoryQuantity,
  variantId,
  trackQuantity,
  bundleable = false,
  bundleToCart = false
) => {
  if (!variantId) {
    // eslint-disable-next-line no-console
    console.error('The variant ID is required for bundle items');
    return;
  }

  // If we don't need to track quantity, return the quantity
  if (!trackQuantity) {
    return Number(quantity);
  }

  let itemCount = (await getLineItemCount(variantId)) || 0; // see how many are in the cart
  const bundleItem = (await getBundleItem(variantId)) || 0; // Look up the item in the bundle when present

  if (bundleable) {
    itemCount = bundleItem ? bundleItem.quantity : 0; // get the current item count within the bundle
  }

  // Don't add the item if we can't fit the bundle into the cart by the requested amount
  if (bundleToCart && itemCount + bundleItem.quantity > maxInventoryQuantity) {
    quantity = 0;
  }

  // We work out how many items that can still be added to the cart and add instead of the initial request - note we don't calculate the difference when transferring items from bundle builder to cart
  if (!bundleToCart && itemCount + quantity > maxInventoryQuantity) {
    const quantityRemaining = maxInventoryQuantity - itemCount; // Still add the remaining amount to the cart
    quantity = Math.max(quantityRemaining, 0);
  }

  return Number(quantity);
};

export {
  addItemsToCartRequest,
  updateItemQuantityRequest,
  dispatchCartClickEventListener,
  onCartUpdate,
  removeVariantsRequest,
  maxQuantityHandler
};
